import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import Skeleton from 'react-loading-skeleton';
import { Button, CardBody } from 'reactstrap';

import globalTranslations from '../../../../i18n/globalTranslations';
import { modelOf } from '../../../../prop-types';
import AnalyticsService from '../../../../services/AnalyticsService';
import CheckoutStore from '../../../../store/CheckoutStore';
import ProductStore from '../../../../store/ProductStore';
import UIStore from '../../../../store/UIStore';
import RequestState from '../../../../types/RequestState';
import { analytics } from '../../../../util/constants';
import getOrderProductRowKey from '../../../../util/orderProductRowKey';
import Icon from '../../../common/Icon';
import RecommendedProductsSlider from '../../../slider/RecommendedProductsSlider';
import CheckoutCartCategoryFilters, {
  EMPTY_CATEGORY_VALUE,
  getEnabledFilters,
  normalizeCategoryFilterValue,
  STORAGE_KEY,
} from '../CheckoutCartCategoryFilters';
import { stringifyAndSetStorage } from '../../../../util/storage';
import CheckoutCartHeader from '../CheckoutCartHeader';
import CheckoutCartModal from '../CheckoutCartModal';
import CheckoutCartProductRow from '../CheckoutCartProductRow';
import CheckoutCartProductRowB2B from '../CheckoutCartProductRowB2B';
import CheckoutRecommendedProductsLabelRow, {
  SLIDES_TO_SHOW,
} from '../CheckoutRecommendedProductsLabelRow';

const CheckoutCart = ({
  analyticsService,
  checkoutStore,
  productStore,
  uiStore,
  onOrderUpdate,
  emptyCart,
  intl,
}) => {
  const { order } = checkoutStore;
  const { show_product_code, productList, show_recommended_products } = order;
  const disabled =
    checkoutStore.cartIsLoading || checkoutStore.state === RequestState.LOADING;
  const sliderIsLoading = productStore.state !== RequestState.LOADED;
  const searchParams = {
    recommendedForIds: order.recommendedProductsIds,
    optimizeResponse: 0,
    excludeContent: 0,
    skipCartProducts: 1,
    inStock: 1,
    includeChildProducts: 0,
  };

  /**
   * Store the ref in state rather than in useRef-hook.
   * We cannot use useRef-hook here since the parent doesn't detect
   * the change in ref.
   */
  const [sliderRef, setSliderRef] = useState(null);
  const lastSliderInnerRef = useRef(null);
  const [sliderIsAnimating, setSliderAnimating] = useState(false);
  const [recommendedCurrentSlide, setRecommendedCurrentSlide] = useState(0);
  const [enabledFilters, setEnabledFilters] = useState(getEnabledFilters);

  // Derive category filter from order.available_filters and current productList so it
  // updates when cart changes (product removed/added). Options and counts follow the
  // filtered categories present in the cart.
  const categoryFilter = useMemo(() => {
    const availableFilters = order?.available_filters;
    if (!availableFilters || availableFilters.length === 0) {
      return null;
    }

    const baseFilter = availableFilters.find(
      (filter) => filter.options && filter.options.length > 0
    );
    if (!baseFilter?.options?.length) {
      return null;
    }

    const list = productList || [];
    const matchesOption = (product, optionValue) => {
      const cat = product.main_category_id;
      if (
        optionValue === 'null' ||
        optionValue === '0' ||
        optionValue === '' ||
        optionValue == null
      ) {
        return cat === null || cat === 0 || cat === undefined;
      }
      return Number(optionValue) === cat;
    };

    const derivedOptions = baseFilter.options.map((opt) => {
      const count = list.filter((p) => matchesOption(p, opt.value)).length;
      return {
        name: opt.name,
        value: opt.value,
        count,
        getCount: count,
      };
    });

    const totalCount = derivedOptions.reduce(
      (sum, o) => sum + (o.count ?? 0),
      0
    );
    return {
      name: baseFilter.name,
      key: baseFilter.key,
      type: baseFilter.type,
      options: derivedOptions,
      totalCount,
    };
  }, [order?.available_filters, productList?.length]);

  // Reset enabled filters when the selected filter(s) no longer have any products in the cart
  useEffect(() => {
    if (!categoryFilter?.options || !enabledFilters?.length) {
      return;
    }
    const optionValuesWithProducts = new Set(
      categoryFilter.options
        .filter((opt) => (opt.count ?? 0) > 0)
        .map((opt) => normalizeCategoryFilterValue(opt.value))
    );
    const hasInvalidSelection = enabledFilters.some(
      (id) => !optionValuesWithProducts.has(normalizeCategoryFilterValue(id))
    );
    if (hasInvalidSelection) {
      setEnabledFilters([]);
      stringifyAndSetStorage(STORAGE_KEY, []);
      window.dispatchEvent(new Event('checkoutCartFiltersChanged'));
    }
  }, [categoryFilter, enabledFilters]);

  // Listen for storage changes to update filters when localStorage is updated
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === STORAGE_KEY) {
        setEnabledFilters(getEnabledFilters());
      }
    };

    // Listen for storage events (from other tabs/windows)
    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom events (from same window)
    const handleCustomStorageChange = () => {
      setEnabledFilters(getEnabledFilters());
    };
    window.addEventListener(
      'checkoutCartFiltersChanged',
      handleCustomStorageChange
    );

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(
        'checkoutCartFiltersChanged',
        handleCustomStorageChange
      );
    };
  }, []);

  // Filter products based on enabled category filters
  const filteredProductList = useMemo(() => {
    // If API doesn't provide filters, don't apply persisted filters either.
    if (!categoryFilter) {
      return productList;
    }

    if (!enabledFilters || enabledFilters.length === 0) {
      // No filters enabled, show all products
      return productList;
    }

    return productList.filter((product) => {
      const productCategoryId = product.main_category_id;

      // Check if product's category matches any enabled filter (use shared normalization)
      return enabledFilters.some((filterId) => {
        const normalizedId = normalizeCategoryFilterValue(filterId);
        if (normalizedId === EMPTY_CATEGORY_VALUE) {
          return (
            productCategoryId === null ||
            productCategoryId === 0 ||
            productCategoryId === undefined
          );
        }

        const categoryId = Number(normalizedId);
        return productCategoryId === categoryId;
      });
    });
  }, [productList, enabledFilters, categoryFilter]);

  const handleFiltersChange = useCallback((newEnabledFilters) => {
    setEnabledFilters(newEnabledFilters);
    // Dispatch custom event for same-window updates
    window.dispatchEvent(new Event('checkoutCartFiltersChanged'));
  }, []);

  const AdditionalProductCardContent = ({ product, position }) => {
    const { openProductPageModal, addProductToOrder } = checkoutStore;
    let addToCartHandler;

    if (product.isCollection || product.isMulti) {
      addToCartHandler = () => openProductPageModal(product.id, position);
    } else {
      addToCartHandler = () =>
        addProductToOrder({ product, quantity: 1 }).then(() => {
          analyticsService.addToCart({
            product,
            additionalProps: {
              quantity: 1,
              activeProductId: product.id,
              itemListId: analytics.checkout.checkoutCartProductsListId,
              itemListName: analytics.checkout.checkoutCartProductsListName,
              position,
            },
          });
        });
    }

    return (
      <div className="CheckoutCart__recommended-product-buttons">
        <Button
          className="CheckoutCart__recommended-product-modal-button"
          color="secondary"
          onClick={() => openProductPageModal(product.id, position)}
        >
          <FormattedMessage {...globalTranslations.viewMoreTitle} />
        </Button>
        <Button
          className="CheckoutCart__recommended-product-cart-button"
          onClick={addToCartHandler}
          color="primary"
          aria-label={intl.formatMessage(globalTranslations.addToCartButton)}
          disabled={
            !product.isCollection &&
            !product.isMulti &&
            !product.canBeOrdered(product.id)
          }
        >
          <Icon name="shopping-cart" />
        </Button>
      </div>
    );
  };

  const renderLabelRow = () => {
    const payload = sliderRef;
    const innerRef = payload?.innerRef;
    const slideCount = payload?.slideCount ?? 0;

    /**
     * Since we're rendering this component upper in the tree,
     * show placeholder skeleton until the child slider component
     * has been fully rendered. Otherwise we won't have the slider
     * instance available.
     */
    if (!innerRef || sliderIsLoading) {
      return <Skeleton height={50} />;
    }

    // afterChange often doesn't fire for programmatic slickGoTo, so isAnimating can stay
    // true and block the next click. Clear it after the slide transition (react-slick default ~500ms).
    const SLIDER_TRANSITION_MS = 500;
    const clearAnimating = () =>
      setTimeout(() => setSliderAnimating(false), SLIDER_TRANSITION_MS);

    const goToPrevSlide = () => {
      const prevIndex = Math.max(0, recommendedCurrentSlide - SLIDES_TO_SHOW);
      setRecommendedCurrentSlide(prevIndex);
      setSliderAnimating(true);
      requestAnimationFrame(() => {
        if (innerRef?.slickGoTo) innerRef.slickGoTo.call(innerRef, prevIndex);
        clearAnimating();
      });
    };
    const goToNextSlide = () => {
      const nextIndex = Math.min(
        recommendedCurrentSlide + SLIDES_TO_SHOW,
        Math.max(0, slideCount - SLIDES_TO_SHOW)
      );
      setRecommendedCurrentSlide(nextIndex);
      setSliderAnimating(true);
      requestAnimationFrame(() => {
        if (innerRef?.slickGoTo) innerRef.slickGoTo.call(innerRef, nextIndex);
        clearAnimating();
      });
    };

    return (
      <CheckoutRecommendedProductsLabelRow
        key={slideCount}
        slideCount={slideCount}
        currentSlide={recommendedCurrentSlide}
        onClickNext={goToNextSlide}
        onClickPrev={goToPrevSlide}
        isLoading={sliderIsLoading}
        isAnimating={sliderIsAnimating}
      />
    );
  };

  const renderRecommendedProducts = () => {
    return (
      <>
        <RecommendedProductsSlider
          shouldRender
          listId={analytics.checkout.checkoutRecommendedProductsListId}
          listName={analytics.checkout.checkoutRecommendedProductsListName}
          onRenderLabelRow={renderLabelRow}
          overrideParams={searchParams}
          cardSettings={{
            additionalProductCardContent: AdditionalProductCardContent,
            checkoutCart: true,
            hideAddToCartButton: true,
            hideAvailability: true,
            hideProperties: true,
            hideReviews: true,
            hideReservation: true,
            forceContent: true,
          }}
          settings={{
            arrows: false,
            dots: false,
            slidesToShow: SLIDES_TO_SHOW,
            slidesToScroll: SLIDES_TO_SHOW,
            infinite: false,
            beforeChange: (current, next) => {
              setSliderAnimating(true);
              setRecommendedCurrentSlide(next);
            },
            afterChange: () => setSliderAnimating(false),
          }}
          productImageSettings={{
            forceAspectRatio: true,
            overrideConfigStoreAspectRatio: true,
            customAspectRatio: '100',
          }}
          onMount={({ innerRef, slideCount }) => {
            // Only update state when the slider instance actually changes to avoid
            // "Maximum update depth exceeded" (ref callback can run multiple times).
            if (lastSliderInnerRef.current === innerRef) return;
            lastSliderInnerRef.current = innerRef;
            setSliderRef({ innerRef, slideCount });
            setRecommendedCurrentSlide(0);
          }}
        />
        {checkoutStore.checkoutCartModal.isOpen && !sliderIsLoading && (
          <CheckoutCartModal />
        )}
      </>
    );
  };

  const renderCart = () => {
    if (order.cartIsEmpty) {
      return emptyCart;
    }

    const ProductRowComponent = order.isB2BCheckout
      ? CheckoutCartProductRowB2B
      : CheckoutCartProductRow;

    const showFiltersRow = !!categoryFilter;

    return (
      <div className="CheckoutCart__products">
        {showFiltersRow && (
          <div className="CheckoutCart__filters-row">
            <CheckoutCartCategoryFilters
              filter={categoryFilter}
              onFiltersChange={handleFiltersChange}
            />
          </div>
        )}
        {uiStore.isLargest && !order.isB2BCheckout && <CheckoutCartHeader />}
        {filteredProductList.map((product) => {
          if (product.quantity === 0) {
            return null;
          }

          return (
            <ProductRowComponent
              key={getOrderProductRowKey(product)}
              product={product}
              onOrderUpdate={onOrderUpdate}
              disabled={disabled}
              showProductCode={show_product_code}
            />
          );
        })}
      </div>
    );
  };

  return (
    <CardBody
      className={classNames(
        'CheckoutCart',
        'CheckoutCart__body',
        !order.isB2BCheckout && 'CheckoutCart--b2c'
      )}
    >
      {renderCart()}
      {show_recommended_products && renderRecommendedProducts()}
    </CardBody>
  );
};

CheckoutCart.propTypes = {
  analyticsService: PropTypes.instanceOf(AnalyticsService).isRequired,
  checkoutStore: modelOf(CheckoutStore).isRequired,
  productStore: modelOf(ProductStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  onOrderUpdate: PropTypes.func.isRequired,
  emptyCart: PropTypes.node.isRequired,
  intl: PropTypes.object.isRequired,
};

export default inject(
  'analyticsService',
  'checkoutStore',
  'productStore',
  'uiStore'
)(injectIntl(observer(CheckoutCart)));
